import { Link } from 'react-router-dom';
import { accountDetailPath } from '../../lib/portal';
import type { Client } from '../../types/crm';
import { accountTypeBadgeStyle, accountTypeShortLabel } from '../../utils/crmHelpers';
import { formatClientSalespersonCode } from '../../utils/salespersonScope';
import { resolveClientDistrict } from '../../utils/singaporeDistrict';
import { AccountAvatar } from './AccountAvatar';
import { AcraStatusBadge } from './AcraStatusBadge';
import { isUenMarkedNa } from '../../lib/uenValue';

interface AccountListRowProps {
  client: Client;
  salespersonCode?: string;
  /** IT Management — omit salesperson column. */
  hideSalesperson?: boolean;
  acraStatus?: string | null;
  depth?: number;
  isSubCompany?: boolean;
  parentName?: string;
  /** Parent account type — brands display this so they stay in sync before DB cascade. */
  parentClientType?: string | null;
  hasSubCompanies?: boolean;
  isLastSub?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelected?: (clientId: string, selected: boolean) => void;
  /** Called just before navigating to account details (persist list scroll / filters). */
  onOpen?: () => void;
}

export function AccountListRow({
  client,
  salespersonCode,
  hideSalesperson = false,
  acraStatus = null,
  isSubCompany = false,
  parentName,
  parentClientType = null,
  hasSubCompanies = false,
  isLastSub = false,
  selectable = false,
  selected = false,
  onToggleSelected,
  onOpen,
}: AccountListRowProps) {
  const displayType =
    (isSubCompany ? parentClientType?.trim() || client.client_type : client.client_type) || undefined;
  const badgeStyle = accountTypeBadgeStyle(displayType);
  const spCode = formatClientSalespersonCode(salespersonCode ?? client.owner_id);
  const { district, source: districtSource } = resolveClientDistrict(client);

  const typeChip = displayType ? (
    <span
      className="account-list-row__type"
      style={badgeStyle}
      title={
        isSubCompany
          ? parentName
            ? `Brand under ${parentName}`
            : 'Brand'
          : undefined
      }
    >
      {accountTypeShortLabel(displayType)}
    </span>
  ) : isSubCompany ? (
    <span
      className="account-list-row__sub-badge"
      title={parentName ? `Under ${parentName}` : 'Sub-company'}
    >
      Brand
    </span>
  ) : null;

  const acraChip =
    isUenMarkedNa(client.uen) ? (
      <span className="account-list-row__uen-na" title="No UEN / not applicable">
        NA
      </span>
    ) : acraStatus ? (
      <AcraStatusBadge status={acraStatus} />
    ) : null;

  return (
    <div
      className={[
        'account-list-row-wrap',
        selectable ? 'account-list-row-wrap--selectable' : '',
        selected ? 'account-list-row-wrap--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {selectable ? (
        <label className="account-list-row__check">
          <input
            type="checkbox"
            checked={selected}
            aria-label={`Select ${client.company_name}`}
            onChange={(e) => onToggleSelected?.(client.id, e.target.checked)}
          />
        </label>
      ) : null}
      <Link
        to={accountDetailPath(client.code)}
        onClick={() => onOpen?.()}
        className={[
          'account-list-row',
          hideSalesperson ? 'account-list-row--no-salesperson' : '',
          isSubCompany ? 'account-list-row--sub' : '',
          isSubCompany && isLastSub ? 'account-list-row--sub-last' : '',
          !isSubCompany && hasSubCompanies ? 'account-list-row--parent' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="account-list-row__type-col">
          {typeChip ?? <span className="account-list-row__type-empty">—</span>}
        </span>

        <div className="account-list-row__account">
          <div className="account-list-row__logo-stack">
            <AccountAvatar client={client} size="sm" />
            {typeChip ? <span className="account-list-row__logo-type">{typeChip}</span> : null}
          </div>
          <div className="account-list-row__main-text">
            <span className="account-list-row__name-line">
              <span className="account-list-row__name">{client.company_name}</span>
              <span className="account-list-row__acra account-list-row__acra--inline">{acraChip}</span>
            </span>
            <span className="account-list-row__code-line">
              <span className="account-list-row__code">{client.code}</span>
            </span>
            {isSubCompany && parentName && (
              <span className="account-list-row__parent-ref">Under {parentName}</span>
            )}
          </div>
        </div>

        <span className="account-list-row__acra account-list-row__acra--column">
          {acraChip ?? '—'}
        </span>

        <span className="account-list-row__district" title={district || undefined}>
          {district ? (
            <>
              <span>{district}</span>
              {districtSource === 'acra' ? (
                <span className="account-list-row__district-acra" title="From ACRA registered address">
                  ACRA
                </span>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </span>
        <span className="account-list-row__district-mobile" title={district || undefined}>
          {district ? (
            <>
              <span>{district}</span>
              {districtSource === 'acra' ? (
                <span className="account-list-row__district-acra" title="From ACRA registered address">
                  ACRA
                </span>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </span>
        {!hideSalesperson ? (
          <span className="account-list-row__owner mono">{spCode}</span>
        ) : null}
        <span className="account-list-row__chevron" aria-hidden>
          ›
        </span>
      </Link>
    </div>
  );
}
